import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Rocket,
  Wallet,
  AlertTriangle,
  CheckCircle2,
  Loader2,
  Upload,
  X,
  RefreshCw,
  Coins,
  Lock,
  ArrowRight,
  Search,
  SlidersHorizontal,
  TrendingUp,
  Clock,
  BarChart2,
  Star,
} from "lucide-react";
import { useSquadToken, type TokenInfo, type CreateTokenParams } from "../hooks/useSquadToken";
import TokenCard from "./TokenCard";
import BuyTokenModal from "./BuyTokenModal";
import WalletModal from "./WalletModal"; // ← NEW

// ... (all constants, types, helpers remain identical)

const MONAD_CHAIN_ID     = 143;
const MONAD_CHAIN_ID_HEX = "0x8f";
const PINATA_JWT         = import.meta.env.VITE_PINATA_JWT as string;

type Step    = "connect" | "checking" | "gate-fail" | "own-token" | "form" | "success";
type SortKey = "newest" | "marketcap" | "price" | "progress";

interface WalletState {
  address: string;
  chainId: number;
  domain:  string;
  hasSBT:  boolean;
}

function shortAddr(addr: string): string {
  return addr.slice(0, 6) + "…" + addr.slice(-4);
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((res, rej) => {
    const reader = new FileReader();
    reader.onload  = () => res(reader.result as string);
    reader.onerror = rej;
    reader.readAsDataURL(file);
  });
}

async function uploadToIPFS(file: File): Promise<string> {
  if (!PINATA_JWT) throw new Error("VITE_PINATA_JWT is not set in .env");
  const body = new FormData();
  body.append("file", file);
  const res = await fetch("https://api.pinata.cloud/pinning/pinFileToIPFS", {
    method:  "POST",
    headers: { Authorization: `Bearer ${PINATA_JWT}` },
    body,
  });
  if (!res.ok) {
    const txt = await res.text().catch(() => "");
    throw new Error(`Pinata upload failed (${res.status}): ${txt}`);
  }
  const data = await res.json() as { IpfsHash: string };
  return `ipfs://${data.IpfsHash}`;
}

const SORT_OPTIONS: { key: SortKey; label: string; icon: React.ReactNode }[] = [
  { key: "newest",    label: "Newest",   icon: <Clock size={12} /> },
  { key: "marketcap", label: "Mkt Cap",  icon: <BarChart2 size={12} /> },
  { key: "price",     label: "Price",    icon: <TrendingUp size={12} /> },
  { key: "progress",  label: "Progress", icon: <Coins size={12} /> },
];

function sortTokens(tokens: TokenInfo[], key: SortKey): TokenInfo[] {
  const arr = [...tokens];
  switch (key) {
    case "newest":    return arr.sort((a, b) => b.createdAt - a.createdAt);
    case "marketcap": return arr.sort((a, b) => Number((b.marketCap ?? 0n) - (a.marketCap ?? 0n)));
    case "price":     return arr.sort((a, b) => Number((b.currentPrice ?? 0n) - (a.currentPrice ?? 0n)));
    case "progress": {
      const pct = (t: TokenInfo) =>
        t.totalSupply === 0n ? 0 : Number((t.tokensSold * 10000n) / (t.totalSupply / 10n ** 18n));
      return arr.sort((a, b) => pct(b) - pct(a));
    }
    default: return arr;
  }
}

export default function TokenCreator() {
  const { checkSBTHolder, getDomainName, checkDomainUsed, createToken, getAllTokens, loading, error, clearError } =
    useSquadToken();

  const [step,          setStep]          = useState<Step>("connect");
  const [wallet,        setWallet]        = useState<WalletState | null>(null);
  const [tokens,        setTokens]        = useState<TokenInfo[]>([]);
  const [tokensLoading, setTokensLoading] = useState(false);
  const [buyToken,      setBuyToken]      = useState<TokenInfo | null>(null);
  const [ownToken,      setOwnToken]      = useState<TokenInfo | null>(null);
  const [gateError,     setGateError]     = useState<string>("");
  const [txHash,        setTxHash]        = useState<string | null>(null);
  const [formError,     setFormError]     = useState<string | null>(null);
  const [imageFile,     setImageFile]     = useState<File | null>(null);
  const [ipfsUploading, setIpfsUploading] = useState(false);
  const imgInputRef                       = useRef<HTMLInputElement>(null);

  const [sortKey,       setSortKey]       = useState<SortKey>("newest");
  const [searchQuery,   setSearchQuery]   = useState("");
  const [showWithImage, setShowWithImage] = useState(false);

  const [formData, setFormData] = useState({
    name:          "",
    symbol:        "",
    description:   "",
    totalSupply:   "1000000000",
    initialBuyMon: "0",
    imagePreview:  "",
  });

  const displayedTokens = useMemo(() => {
    let list = [...tokens];
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter(
        (t) =>
          t.name.toLowerCase().includes(q) ||
          t.symbol.toLowerCase().includes(q) ||
          t.creatorDomain.toLowerCase().includes(q)
      );
    }
    if (showWithImage) list = list.filter((t) => !!t.imageURI);
    return sortTokens(list, sortKey);
  }, [tokens, sortKey, searchQuery, showWithImage]);

  const loadTokens = useCallback(async (): Promise<TokenInfo[]> => {
    setTokensLoading(true);
    try {
      const list = await getAllTokens();
      setTokens(list);
      return list;
    } catch {
      return [];
    } finally {
      setTokensLoading(false);
    }
  }, [getAllTokens]);

  useEffect(() => { loadTokens(); }, [loadTokens]);

  const connectWallet = useCallback(async () => {
    setGateError("");
    if (!window.ethereum) {
      setGateError("No EIP-1193 wallet found. Please install Rabby or MetaMask.");
      return;
    }
    setStep("checking");
    try {
      const accounts   = (await window.ethereum.request({ method: "eth_requestAccounts" })) as string[];
      if (!accounts.length) throw new Error("No accounts returned");
      const address    = accounts[0].toLowerCase();
      const chainIdHex = (await window.ethereum.request({ method: "eth_chainId" })) as string;
      const chainId    = parseInt(chainIdHex, 16);
      if (chainId !== MONAD_CHAIN_ID) {
        try {
          await window.ethereum.request({
            method: "wallet_switchEthereumChain",
            params: [{ chainId: MONAD_CHAIN_ID_HEX }],
          });
        } catch {
          setGateError(`Please switch to Monad Mainnet (chainId ${MONAD_CHAIN_ID}) in your wallet.`);
          setStep("gate-fail");
          return;
        }
      }
      const hasSBT = await checkSBTHolder(address);
      if (!hasSBT) {
        setGateError("You don't hold a .10ksquad domain. Only 10K Squad members can launch tokens.");
        setStep("gate-fail");
        setWallet({ address, chainId: MONAD_CHAIN_ID, domain: "", hasSBT: false });
        return;
      }
      const domain      = await getDomainName(address);
      const domainLower = domain.toLowerCase();
      const alreadyUsed = await checkDomainUsed(domainLower);

      if (alreadyUsed) {
        setWallet({ address, chainId: MONAD_CHAIN_ID, domain, hasSBT: true });
        const list       = await getAllTokens();
        setTokens(list);
        const domainFull = `${domain}.10ksquad`.toLowerCase();
        const found      = list.find((t) => t.creatorDomain.toLowerCase() === domainFull);
        if (found) {
          setOwnToken(found);
          setStep("own-token");
        } else {
          setGateError(`${domain}.10ksquad has already launched a token. Each domain can only launch once.`);
          setStep("gate-fail");
        }
        return;
      }

      setWallet({ address, chainId: MONAD_CHAIN_ID, domain, hasSBT: true });
      setStep("form");
    } catch (e: unknown) {
      setGateError(e instanceof Error ? e.message : String(e));
      setStep("gate-fail");
    }
  }, [checkSBTHolder, getDomainName, checkDomainUsed, getAllTokens]);

  const handleImageUpload = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      setFormError("Image must be under 10 MB.");
      return;
    }
    setFormError(null);
    try {
      const preview = await fileToBase64(file);
      setFormData((d) => ({ ...d, imagePreview: preview }));
      setImageFile(file);
    } catch {
      setFormError("Failed to read image file.");
    }
  }, []);

  const handleCreate = useCallback(async () => {
    setFormError(null);
    if (!formData.name.trim())      return setFormError("Token name is required.");
    if (!formData.symbol.trim())    return setFormError("Symbol is required.");
    if (formData.symbol.length > 8) return setFormError("Symbol must be ≤ 8 characters.");
    const supply = BigInt(formData.totalSupply.replace(/,/g, "") || "0");
    if (supply <= 0n)               return setFormError("Supply must be greater than 0.");

    const initBuy    = parseFloat(formData.initialBuyMon) || 0;
    const initBuyWei = initBuy > 0 ? BigInt(Math.floor(initBuy * 1e18)) : 0n;

    const params: CreateTokenParams = {
      name:          formData.name.trim(),
      symbol:        formData.symbol.trim().toUpperCase(),
      totalSupply:   supply,
      description:   formData.description.trim(),
      imageURI:      "",
      initialBuyMon: initBuyWei,
    };

    try {
      const hash = await createToken(params);
      setTxHash(hash);
      setStep("success");

      if (imageFile) {
        setIpfsUploading(true);
        try {
          await uploadToIPFS(imageFile);
        } catch {
          // non-fatal
        } finally {
          setIpfsUploading(false);
        }
      }

      setTimeout(loadTokens, 3000);
    } catch (e: unknown) {
      setFormError(e instanceof Error ? e.message : "Transaction failed.");
    }
  }, [formData, imageFile, createToken, loadTokens]);

  const hasLeftPanel = step !== "connect" && step !== "checking" && step !== "gate-fail";

  return (
    <div className="h-screen overflow-hidden bg-grid bg-radial-pink relative flex flex-col">

      {/* ── Top bar ── */}
      <div className="shrink-0 px-4 sm:px-6 pt-6 pb-4 flex flex-col sm:flex-row sm:items-end gap-3">
        <div className="flex-1 min-w-0">
          <div className="inline-flex items-center gap-2 bg-white/5 border border-[--color-border] rounded-full px-3 py-1 mb-2">
            <Rocket size={10} className="text-[--color-pink]" />
            <span className="text-[10px] font-body text-white/50">Squad-exclusive launchpad</span>
          </div>
          <h1 className="font-display text-2xl sm:text-3xl shimmer-text leading-tight">
            SQUAD TOKEN CREATOR
          </h1>
          <p className="text-white/40 font-body text-xs mt-0.5">
            Launch your memecoin on Monad —{" "}
            <span className="text-[--color-pink]">.10ksquad</span> holders only.
          </p>
        </div>

        {/* ── Wallet pill — top-right ── */}
        <div className="shrink-0">
          {step === "connect" && (
            // ↓ CHANGED: replaced inline button with WalletModal compact
            <WalletModal compact onConnect={connectWallet} />
          )}
          {step === "checking" && (
            <div className="flex items-center gap-2 bg-white/5 border border-[--color-border] rounded-xl px-4 py-2">
              <Loader2 size={13} className="text-[--color-pink] animate-spin" />
              <span className="text-xs text-white/50 font-body">Verifying…</span>
            </div>
          )}
          {step === "gate-fail" && (
            <button
              onClick={() => { setStep("connect"); setGateError(""); }}
              className="btn-ghost py-2 px-4 flex items-center gap-2 text-sm"
            >
              <RefreshCw size={13} /> Try Again
            </button>
          )}
          {(step === "form" || step === "own-token" || step === "success") && wallet && (
            <div className="flex items-center gap-2 bg-[--color-pink]/10 border border-[--color-pink]/30 rounded-xl px-3 py-2">
              <Star size={12} className="text-[--color-pink] shrink-0" />
              <span className="font-mono text-xs text-[--color-pink] font-bold truncate max-w-[140px]">
                {wallet.domain}.10ksquad
              </span>
              <span className="text-[10px] text-white/30 font-mono">{shortAddr(wallet.address)}</span>
            </div>
          )}
        </div>
      </div>

      {/* ── Gate-fail error banner ── */}
      <AnimatePresence>
        {step === "gate-fail" && gateError && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            className="shrink-0 mx-4 sm:mx-6 mb-2"
          >
            <ErrorBanner message={gateError} onClose={() => { setStep("connect"); setGateError(""); }} />
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Main content ── */}
      <div className="flex-1 overflow-hidden px-4 sm:px-6 pb-4">
        <div className={`h-full flex gap-4 xl:gap-6 ${hasLeftPanel ? "flex-col lg:flex-row" : "flex-col"}`}>

          {/* ── Left panel ── */}
          <AnimatePresence mode="wait">
            {step === "form" && wallet && (
              <motion.div
                key="form"
                initial={{ opacity: 0, x: -16 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -16 }}
                transition={{ duration: 0.25 }}
                className="lg:w-[360px] xl:w-[380px] shrink-0 overflow-y-auto scrollbar-none"
              >
                <div className="card-glass glow-border rounded-2xl p-4 sm:p-5">
                  <h2 className="font-display text-lg text-white mb-4 tracking-wide">Launch Your Token</h2>
                  <div className="flex flex-col gap-3">
                    <div className="grid grid-cols-2 gap-3">
                      <FormField label="Token Name">
                        <input
                          type="text"
                          placeholder="My Squad Token"
                          value={formData.name}
                          onChange={(e) => setFormData((d) => ({ ...d, name: e.target.value }))}
                          className="form-input"
                        />
                      </FormField>
                      <FormField label="Symbol" hint="Max 8">
                        <input
                          type="text"
                          placeholder="SQUD"
                          maxLength={8}
                          value={formData.symbol}
                          onChange={(e) => setFormData((d) => ({ ...d, symbol: e.target.value.toUpperCase() }))}
                          className="form-input font-mono"
                        />
                      </FormField>
                    </div>

                    <FormField label="Description" hint="Max 200">
                      <textarea
                        placeholder="What's your token about?"
                        maxLength={200}
                        rows={2}
                        value={formData.description}
                        onChange={(e) => setFormData((d) => ({ ...d, description: e.target.value }))}
                        className="form-input resize-none"
                      />
                    </FormField>

                    <div className="grid grid-cols-2 gap-3">
                      <FormField label="Supply" hint="Default: 1B">
                        <input
                          type="number"
                          min="1"
                          placeholder="1000000000"
                          value={formData.totalSupply}
                          onChange={(e) => setFormData((d) => ({ ...d, totalSupply: e.target.value }))}
                          className="form-input font-mono"
                        />
                      </FormField>
                      <FormField label="Initial Buy" hint="MON">
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          placeholder="0.0"
                          value={formData.initialBuyMon}
                          onChange={(e) => setFormData((d) => ({ ...d, initialBuyMon: e.target.value }))}
                          className="form-input font-mono"
                        />
                      </FormField>
                    </div>

                    <FormField label="Token Image" hint="IPFS after launch">
                      <input ref={imgInputRef} type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
                      {formData.imagePreview ? (
                        <div className="relative group">
                          <img
                            src={formData.imagePreview}
                            alt="preview"
                            className="w-full h-24 object-cover rounded-xl border border-[--color-border]"
                          />
                          <div className="absolute bottom-2 left-2 bg-white/10 backdrop-blur-sm rounded-lg px-2 py-0.5 flex items-center gap-1">
                            <CheckCircle2 size={10} className="text-white/60" />
                            <span className="text-[10px] text-white/60 font-body">Ready</span>
                          </div>
                          <button
                            onClick={() => { setFormData((d) => ({ ...d, imagePreview: "" })); setImageFile(null); if (imgInputRef.current) imgInputRef.current.value = ""; }}
                            className="absolute top-2 right-2 bg-black/70 rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <X size={12} className="text-white" />
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => imgInputRef.current?.click()}
                          className="w-full h-16 border border-dashed border-[--color-border] rounded-xl flex items-center justify-center gap-2 hover:border-[--color-pink]/50 transition-colors"
                        >
                          <Upload size={14} className="text-white/30" />
                          <span className="text-xs text-white/30 font-body">Click to upload</span>
                        </button>
                      )}
                    </FormField>

                    <div className="bg-white/5 rounded-xl px-3 py-2 flex items-center justify-between">
                      <span className="text-xs text-white/40 font-body">Creator Badge</span>
                      <span className="font-mono text-xs text-[--color-pink] font-bold">{wallet.domain}.10ksquad</span>
                    </div>

                    {(formError || error) && (
                      <ErrorBanner message={formError ?? error ?? ""} onClose={() => { setFormError(null); clearError(); }} />
                    )}

                    <button
                      onClick={handleCreate}
                      disabled={loading}
                      className="btn-pink w-full py-2.5 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {loading
                        ? <><Loader2 size={13} className="animate-spin" /> Launching…</>
                        : <><Rocket size={13} /> Launch Token <ArrowRight size={13} /></>
                      }
                    </button>
                  </div>
                </div>
              </motion.div>
            )}

            {step === "own-token" && ownToken && wallet && (
              <motion.div
                key="own-token"
                initial={{ opacity: 0, x: -16 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -16 }}
                transition={{ duration: 0.25 }}
                className="lg:w-[360px] xl:w-[380px] shrink-0"
              >
                <div className="card-glass glow-border rounded-2xl p-4 sm:p-5">
                  <div className="flex items-center gap-1.5 mb-4">
                    <Star size={13} className="text-[--color-pink]" />
                    <span className="font-display text-sm text-[--color-pink] tracking-widest uppercase">My Token</span>
                  </div>

                  {ownToken.imageURI ? (
                    <img
                      src={ownToken.imageURI.replace("ipfs://", "https://ipfs.io/ipfs/")}
                      alt={ownToken.name}
                      className="w-full h-36 object-cover rounded-xl border border-[--color-border] mb-3"
                      onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
                    />
                  ) : (
                    <div className="w-full h-24 rounded-xl bg-white/5 border border-[--color-border] flex items-center justify-center mb-3">
                      <Coins size={28} className="text-white/15" />
                    </div>
                  )}

                  <p className="font-display text-2xl text-white leading-tight">{ownToken.name}</p>
                  <p className="font-mono text-sm text-[--color-pink] font-bold mb-1">${ownToken.symbol}</p>
                  {ownToken.description && (
                    <p className="text-xs text-white/40 font-body line-clamp-2 mb-4">{ownToken.description}</p>
                  )}

                  <button
                    onClick={() => setBuyToken(ownToken)}
                    className="btn-pink w-full py-2.5 flex items-center justify-center gap-2 text-sm"
                  >
                    <Coins size={13} /> Buy {ownToken.symbol}
                  </button>
                </div>
              </motion.div>
            )}

            {step === "success" && (
              <motion.div
                key="success"
                initial={{ opacity: 0, scale: 0.97 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.25 }}
                className="lg:w-[360px] xl:w-[380px] shrink-0"
              >
                <div className="card-glass glow-border rounded-2xl p-5 flex flex-col items-center text-center">
                  <CheckCircle2 className="w-12 h-12 text-green-400 mb-3" />
                  <h2 className="font-display text-xl text-white mb-1">Token Launched! 🚀</h2>
                  <p className="text-xs text-white/40 font-body mb-4 leading-relaxed">
                    Your token is being deployed on Monad. It'll appear in the feed shortly.
                  </p>
                  {ipfsUploading && (
                    <div className="flex items-center gap-2 bg-white/5 rounded-xl px-3 py-2 w-full mb-3">
                      <Loader2 size={11} className="text-[--color-pink] animate-spin shrink-0" />
                      <span className="text-xs text-white/40 font-body">Uploading image to IPFS…</span>
                    </div>
                  )}
                  {txHash && (
                    <div className="bg-white/5 rounded-xl p-3 w-full mb-3">
                      <p className="text-[10px] text-white/40 font-body mb-1">Tx Hash</p>
                      <p className="font-mono text-[11px] text-[--color-pink] break-all">{txHash}</p>
                    </div>
                  )}
                  <div className="bg-white/5 border border-[--color-border] rounded-xl px-3 py-2.5 w-full">
                    <p className="text-xs text-white/40 font-body leading-relaxed">
                      <span className="text-[--color-pink] font-mono font-bold">{wallet?.domain}.10ksquad</span>{" "}
                      has used its one launch slot.
                    </p>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* ── Right panel: token feed ── */}
          <div className="flex-1 min-w-0 flex flex-col overflow-hidden">
            <div className="shrink-0">
              <div className="flex items-center gap-3 mb-3">
                <Coins size={15} className="text-[--color-pink]" />
                <h2 className="font-display text-lg text-white tracking-wide">Squad Tokens</h2>
                <span className="bg-[--color-pink]/20 text-[--color-pink] text-xs font-mono px-2 py-0.5 rounded-full">
                  {displayedTokens.length}
                  {displayedTokens.length !== tokens.length && (
                    <span className="text-white/40">/{tokens.length}</span>
                  )}
                </span>
                <button
                  onClick={loadTokens}
                  disabled={tokensLoading}
                  className="ml-auto btn-ghost py-1.5 px-3 text-xs flex items-center gap-1.5"
                >
                  <RefreshCw size={11} className={tokensLoading ? "animate-spin" : ""} />
                  Refresh
                </button>
              </div>

              <div className="flex gap-2 mb-2">
                <div className="relative flex-1">
                  <Search size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30 pointer-events-none" />
                  <input
                    type="text"
                    placeholder="Search name, symbol, creator…"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="form-input pl-8 py-2 text-xs w-full"
                  />
                  {searchQuery && (
                    <button onClick={() => setSearchQuery("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60">
                      <X size={11} />
                    </button>
                  )}
                </div>
                <button
                  onClick={() => setShowWithImage((v) => !v)}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-xl border text-xs font-body whitespace-nowrap transition-all ${
                    showWithImage
                      ? "bg-[--color-pink]/20 border-[--color-pink]/50 text-[--color-pink]"
                      : "bg-white/5 border-[--color-border] text-white/50 hover:text-white/70"
                  }`}
                >
                  <SlidersHorizontal size={11} />
                  <span className="hidden sm:inline">With Image</span>
                </button>
              </div>

              <div className="flex gap-1.5 mb-3 overflow-x-auto pb-0.5 scrollbar-none">
                {SORT_OPTIONS.map((opt) => (
                  <button
                    key={opt.key}
                    onClick={() => setSortKey(opt.key)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-body whitespace-nowrap transition-all ${
                      sortKey === opt.key
                        ? "bg-[--color-pink] text-white shadow-[0_0_12px_#ff268050]"
                        : "bg-white/5 text-white/50 hover:bg-white/10 hover:text-white/80"
                    }`}
                  >
                    {opt.icon}
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex-1 overflow-y-auto scrollbar-none min-h-0">
              {tokensLoading && tokens.length === 0 ? (
                <div className="flex items-center justify-center h-48">
                  <Loader2 className="w-7 h-7 text-[--color-pink] animate-spin" />
                </div>
              ) : displayedTokens.length === 0 ? (
                <div className="card-glass glow-border rounded-2xl p-10 text-center">
                  {tokens.length === 0 ? (
                    <>
                      <Rocket size={28} className="text-white/20 mx-auto mb-3" />
                      <p className="font-display text-base text-white/30">No tokens yet</p>
                      <p className="text-xs text-white/20 font-body mt-1">Be the first to launch a Squad token.</p>
                    </>
                  ) : (
                    <>
                      <Search size={24} className="text-white/20 mx-auto mb-3" />
                      <p className="font-display text-base text-white/30">No results</p>
                      <p className="text-xs text-white/20 font-body mt-1">Try a different search or filter.</p>
                      <button
                        onClick={() => { setSearchQuery(""); setShowWithImage(false); }}
                        className="mt-3 text-xs text-[--color-pink] hover:underline font-body"
                      >
                        Clear filters
                      </button>
                    </>
                  )}
                </div>
              ) : (
                <motion.div
                  layout
                  className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3 pb-2"
                >
                  <AnimatePresence>
                    {displayedTokens.map((token) => (
                      <TokenCard
                        key={token.tokenContract}
                        token={token}
                        onBuy={setBuyToken}
                      />
                    ))}
                  </AnimatePresence>
                </motion.div>
              )}
            </div>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {buyToken && (
          <BuyTokenModal
            key={buyToken.tokenContract}
            token={buyToken}
            walletAddress={wallet?.address}
            onClose={() => setBuyToken(null)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

function FormField({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-baseline justify-between">
        <label className="text-xs font-body text-white/60">{label}</label>
        {hint && <span className="text-[10px] text-white/30 font-body">{hint}</span>}
      </div>
      {children}
    </div>
  );
}

function ErrorBanner({ message, onClose }: { message: string; onClose?: () => void }) {
  return (
    <div className="flex items-start gap-2 bg-red-500/10 border border-red-500/30 rounded-xl p-3">
      <AlertTriangle size={13} className="text-red-400 shrink-0 mt-0.5" />
      <p className="text-xs text-red-300 font-body flex-1 leading-relaxed">{message}</p>
      {onClose && (
        <button onClick={onClose} className="text-red-400/60 hover:text-red-400 shrink-0">
          <X size={13} />
        </button>
      )}
    </div>
  );
}